import { CREATE_DATASET_WITH_SCHEMA_ENABLED } from 'js/constants/config';
import { completeTxBuilderRecipeGroups } from 'js/lib/RecipeBuilderUtils';
import { isCapsuleCluster } from '../../lib/PipelineUtils';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import * as actionTypes from '../../constants/actionTypes';
import {
  ACTIVATE_DATA_SET,
  COPY_DATA_SET,
  COPY_DATA_SET_TO_CLIPBOARD_INFO,
  PASTE_DATA_SET_TRANSFORM,
  PASTE_DATA_SET_TRANSFORM_INFO,
  PAUSE_DATA_SET,
  REGISTER_DATASET_ON_CATALOG,
} from '../../constants/processTypes';
import { POST, PUT } from '../../constants/requestMethods';
import { setReferencedResourceIds } from '../../lib/ResourceUtils';
import { buildOutputValidationSchema } from '../../lib/SchemaValidationUtils';
import TransformsUtils from '../../lib/TransformsUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { getCurrentOrg, getCurrentUser } from '../../reducers/selectors';
import { dataSetSchema } from '../../schemas';
import { copyTxSpecToTransform, fetchDataSet, updateDataSetInfo } from '../dataSets';
import { addProcess, removeProcess } from '../processes';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { getCodeContainer } from '../transform';
import { EMPTY_DATA_SET_SCHEMA } from 'js/constants/mockDataSchemas';

export const activateDataset = (id) => (dispatch, getState) => {
  const {
    entities: { dataSets },
  } = getState();
  const dataSet = dataSets[id];

  dispatch(
    addProcess(ACTIVATE_DATA_SET, `Activating Nexset "${dataSet.name}"`, {
      id,
    }),
  );

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACTIVATE_DATASET_REQUEST,
        actionTypes.UPDATE_DATASET_INFO_SUCCESS,
        actionTypes.UPDATE_DATASET_INFO_FAILURE,
      ],
      endpoint: `/data_sets/${id}/activate?expand=1`,
      schema: dataSetSchema,
      requestMethod: PUT,
    },
  }).finally(() => {
    dispatch(removeProcess(ACTIVATE_DATA_SET, { id }));
  });
};

export const pauseDataset = (id) => (dispatch, getState) => {
  const {
    entities: { dataSets },
  } = getState();
  const dataSet = dataSets[id];

  dispatch(addProcess(PAUSE_DATA_SET, `Pausing Nexset "${dataSet.name}"`, { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PAUSE_DATASET_REQUEST,
        actionTypes.UPDATE_DATASET_INFO_SUCCESS,
        actionTypes.UPDATE_DATASET_INFO_FAILURE,
      ],
      endpoint: `/data_sets/${id}/pause?expand=1`,
      schema: dataSetSchema,
      requestMethod: PUT,
    },
  }).finally(() => {
    dispatch(removeProcess(PAUSE_DATA_SET, { id }));
  });
};

export const addDataset =
  (
    info = null,
    parentDataSetId = null,
    outputValidations = null,
    outputSchema = null,
    rawOutputSchema = null,
    rawOutputSamples = null,
  ) =>
  (dispatch, getState) => {
    if (!info || !parentDataSetId) {
      dispatch({
        type: actionTypes.ADD_DATASET_FAILURE,
        error: 'Insufficient Nexset information',
      });
      // TODO: create error class
      return Promise.reject(new Error('Insufficient Nexset information'));
    }

    const {
      name,
      description,
      transform_id,
      transform,
      has_custom_transform,
      annotations,
      transformSpec,
    } = info;

    let payload = {
      name: name ? name : '',
      description: description ? description : '',
      parent_data_set_id: parentDataSetId,
    };

    if (transform_id) {
      const {
        entities: { transforms },
      } = getState();

      if (transforms && transforms[transform_id] && transforms[transform_id].reusable) {
        payload.transform_id = transform_id;
      }
    }

    if (!payload.transform_id && transform) {
      payload.transform = transform;
    }

    if (has(info, 'has_custom_transform')) {
      payload.has_custom_transform = !!has_custom_transform;
    }

    if (has(info, 'annotations') && annotations) {
      payload.output_schema_annotations = annotations;
    }

    if (isEmpty(outputValidations)) {
      payload.output_schema_validation_enabled = false;
      payload.output_validation_schema = {};
    } else {
      payload.output_schema_validation_enabled = true;
      payload.output_validation_schema = buildOutputValidationSchema(
        outputValidations,
        outputSchema,
      );
    }

    const { recipeBuilder } = getState();
    const isAsync = recipeBuilder?.isAsync;

    if (isAsync) {
      payload.runtime_config = {
        'node.tag': 'async',
      };
    }

    payload.custom_config = { txSpec: transformSpec };

    if (CREATE_DATASET_WITH_SCHEMA_ENABLED) {
      payload.output_schema = isEmpty(rawOutputSchema) ? EMPTY_DATA_SET_SCHEMA : rawOutputSchema;

      if (isEmpty(rawOutputSamples)) {
        const { samples } = getState();
        const nexlaMetaData = samples[parentDataSetId]?.original?.out?.[0]?.nexlaMetaData;
        payload.data_samples = [{ nexlaMetaData, rawMessage: {} }];
      } else {
        payload.data_samples = map(rawOutputSamples, ({ rawMessage, nexlaMetaData }) => ({
          rawMessage,
          nexlaMetaData,
        }));
      }
    }

    const dataSets = getState().entities?.dataSets;
    const isCapsule = isCapsuleCluster(dataSets?.[parentDataSetId]);
    const capsuleSettingsEnabled = getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS');
    const endpoint =
      isCapsule && capsuleSettingsEnabled
        ? `/data_sets?expand=1&activate_now=true`
        : `/data_sets?expand=1`;

    payload = setReferencedResourceIds(payload);

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ADD_DATASET_REQUEST,
          actionTypes.ADD_DATASET_SUCCESS,
          actionTypes.ADD_DATASET_FAILURE,
        ],
        endpoint,
        schema: dataSetSchema,
        requestMethod: POST,
        payload,
      },
    })
      .then((response) => dispatch(copyTxSpecToTransform(response)))
      .catch((error) => {
        console.log('Nexset failed');
        console.log(error);
        return Promise.reject(error);
      });
  };

export const addRawDataset = (payload) => (dispatch) => {
  const newPayload = setReferencedResourceIds(payload);

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_DATASET_REQUEST,
        actionTypes.ADD_DATASET_SUCCESS,
        actionTypes.ADD_DATASET_FAILURE,
      ],
      endpoint: `/data_sets?expand=1`,
      schema: dataSetSchema,
      requestMethod: POST,
      payload: newPayload,
    },
  });
};

export const copyDataset =
  (fromDatasetId = null, parentDataSetId = null) =>
  (dispatch, getState) => {
    if (!fromDatasetId) {
      return dispatch({
        type: actionTypes.ADD_DATASET_FAILURE,
        error: 'Insufficient Nexset information',
      });
    }

    const state = getState();
    const {
      entities: { dataSets, transforms },
    } = state;
    const dataSet = dataSets[fromDatasetId];
    const parentDataSet = parentDataSetId && dataSets[parentDataSetId];

    let fromDataset;

    if (parentDataSetId) {
      //Pasting transform of fromDataset to this parentDataset.
      dispatch(
        addProcess(
          PASTE_DATA_SET_TRANSFORM,
          `Applying transform of Nexset "${dataSet.name}" to Nexset "${parentDataSet.name}"`,
          {
            fromDatasetId,
            parentDataSetId,
          },
        ),
      );

      return dispatch(fetchDataSet(fromDatasetId))
        .then((response) => {
          const {
            entities: { dataSets },
          } = getState();
          fromDataset = dataSets[fromDatasetId];

          if (fromDataset.transform_id) {
            return dispatch(getCodeContainer(fromDataset.transform_id));
          }
        })
        .catch((error) => {
          console.log('fetch transform failed');
          console.log(error);
        })
        .then((response) => {
          const newDatasetInfo = parentDataSetId
            ? {
                name: `Copy of ${parentDataSet.name}`,
                description: `Nexset generated by copying transform from Nexset "${fromDataset.name}"`,
              }
            : {
                name: `Copy of ${fromDataset.name}`,
                description: fromDataset.description,
              };

          //TODO: execute copy action.
          //COPY should take Nexset definition (custom transform/transform id, transform object, )

          newDatasetInfo.transform = fromDataset.transform;
          newDatasetInfo.has_custom_transform = fromDataset.has_custom_transform;
          newDatasetInfo.transform_id = fromDataset.transform_id;

          const txSpec = fromDataset.custom_config?.txSpec;
          let recipeGroups;

          if (txSpec?.v === 2) {
            recipeGroups = txSpec.spec;
          } else {
            recipeGroups = TransformsUtils.getUpgradedTxSpec(fromDataset, transforms);
          }

          recipeGroups = completeTxBuilderRecipeGroups(recipeGroups, transforms);

          newDatasetInfo.transformSpec = { v: 2, spec: recipeGroups };

          let effectiveParentDatasetId = parentDataSetId;

          if (!effectiveParentDatasetId) {
            const effectiveParentDataSet =
              has(fromDataset, 'parent_data_sets') && !isEmpty(fromDataset.parent_data_sets)
                ? fromDataset.parent_data_sets[0]
                : null;

            if (effectiveParentDataSet) {
              effectiveParentDatasetId =
                isPlainObject(effectiveParentDataSet) && has(effectiveParentDataSet, 'id')
                  ? effectiveParentDataSet.id
                  : effectiveParentDataSet;
            }
          }

          // Get feature flag value for multi-block transforms
          const enableMultiBlock = getFeatureFlagValue('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

          const { outputValidations } = TransformsUtils.getTxFromRecipe(
            recipeGroups,
            state.entities.transforms,
            false, // isLegacyMode
            enableMultiBlock,
          );

          const outputSchema = get(getState().dataSetSchemas[fromDataset.id], 'out');

          return dispatch(
            addDataset(
              newDatasetInfo,
              effectiveParentDatasetId,
              outputValidations,
              outputSchema,
              fromDataset.output_schema,
              fromDataset.data_samples,
            ),
          );
        })
        .then((response) => {
          if (parentDataSetId) {
            const {
              result,
              entities: { dataSets },
            } = response;
            const dataSet = dataSets[result];

            dispatch(
              removeProcess(PASTE_DATA_SET_TRANSFORM, {
                fromDatasetId,
                parentDataSetId,
              }),
            );
            dispatch(
              addProcess(
                PASTE_DATA_SET_TRANSFORM_INFO,
                `Generated Dataset "${dataSet.name}"`,
                {
                  fromDatasetId,
                  parentDataSetId,
                },
                true,
              ),
            );
          }
        })
        .catch((error) => {
          console.log('copy Nexset failed');
          console.log(error);
        })
        .finally(() => {
          dispatch(
            removeProcess(PASTE_DATA_SET_TRANSFORM, {
              fromDatasetId,
              parentDataSetId,
            }),
          );
        });
    } else {
      //Simple clone of Nexset with the same shared parent
      dispatch(
        addProcess(COPY_DATA_SET, `Duplicating Nexset "${dataSet.name}"`, {
          fromDatasetId,
        }),
      );
      let payload = {
        copy_access_controls: false,
        reuse_data_credentials: true,
        copy_other_data_sinks: false,
      };

      const user = getCurrentUser(state);
      const org = getCurrentOrg(state);
      if (!isEmpty(user) && !isEmpty(org)) {
        payload.owner_id = user.id;
        payload.org_id = org.id;
      }
      return dispatch({
        [CALL_PRIVATE_API]: {
          types: [
            actionTypes.COPY_DATA_SET_REQUEST,
            actionTypes.COPY_DATA_SET_SUCCESS,
            actionTypes.COPY_DATA_SET_FAILURE,
          ],
          endpoint: `/data_sets/${fromDatasetId}/copy`,
          schema: dataSetSchema,
          requestMethod: POST,
          payload,
        },
      }).finally(() => {
        dispatch(removeProcess(COPY_DATA_SET, { fromDatasetId }));
      });
    }
  };

export const copyDatasetToClipboard = (id) => (dispatch, getState) => {
  const {
    entities: { dataSets },
  } = getState();
  const dataSet = dataSets[id];

  dispatch({
    type: actionTypes.COPY_DATA_SET_TO_CLIPBOARD,
    payload: { id },
  });
  dispatch(
    addProcess(
      COPY_DATA_SET_TO_CLIPBOARD_INFO,
      `Transform of Nexset "${dataSet.name}" copied to clipboard`,
      { id },
      true,
    ),
  );
};

export const submitToCatalog =
  (id, async = true) =>
  (dispatch, getState) => {
    const {
      entities: { dataSets },
    } = getState();
    const dataSet = dataSets[id];

    dispatch(
      addProcess(REGISTER_DATASET_ON_CATALOG, `Registering "${dataSet.name}"`, {
        id,
      }),
    );

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.REGISTER_DATASET_ON_CATALOG_REQUEST,
          actionTypes.REGISTER_DATASET_ON_CATALOG_SUCCESS,
          actionTypes.REGISTER_DATASET_ON_CATALOG_FAILURE,
        ],
        endpoint: `/data_sets/${id}/sync_with_catalog`,
        schema: null,
        requestMethod: POST,
        payload: {
          async: async,
          businessReason: dataSet.name,
        },
      },
    })
      .then(
        (res) => {
          return dispatch(fetchDataSet(id));
        },
        (err) => {
          return dispatch(fetchDataSet(id));
        },
      )
      .finally(() => {
        dispatch(removeProcess(REGISTER_DATASET_ON_CATALOG, { id }));
      });
  };

export const insertDatasetBetween = (parentDataSetId, childDataSetId, payload) => (dispatch) => {
  // create a data set with the given parent_data_set_id and update the
  // child data set to have parent_data_set_id: <created_data_set_id>
  return dispatch(
    addRawDataset({
      ...payload,
      parent_data_set_id: parentDataSetId,
    }),
  ).then(({ result }) => {
    dispatch(updateDataSetInfo(childDataSetId, { parent_data_set_id: result }));
  });
};

export function addRerankerDataset(payload) {
  return (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ADD_DATASET_REQUEST,
          actionTypes.ADD_DATASET_SUCCESS,
          actionTypes.ADD_DATASET_FAILURE,
        ],
        endpoint: `/data_sets?expand=1`,
        schema: dataSetSchema,
        requestMethod: POST,
        payload,
      },
    });
  };
}

export function updateDataset(nexsetId, payload) {
  return (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_DATASET_REQUEST,
          actionTypes.UPDATE_DATASET_SUCCESS,
          actionTypes.UPDATE_DATASET_FAILURE,
        ],
        endpoint: `/data_sets/${nexsetId}?expand=1`,
        schema: dataSetSchema,
        requestMethod: PUT,
        payload,
      },
    });
  };
}
